// @defect https://linear.app/ecoplanet/issue/EC-5003/add-overall-group-view-and-all-possible-energy-types-in-series
import { useSeriesContext } from '@/src/contexts/SeriesContext'
import { SeriesTypeEnum } from '@/src/features/analytics/enums/seriesType.enum'
import { type TimeSeriesOptionGroup, getTimeSeriesSelectionKey } from '@/src/types/timeSeriesSelection.interface'
import { getNormalizedSourceGranularityMinutes } from '@/src/utils/sourceGranularity.utils'
import { useMemo } from 'react'

export interface ITimeSeriesData {
  timeSeries: {
    id: string
    name: string
    locationId: number
    seriesType: SeriesTypeEnum
    label: string
    buildingId?: number
    isVirtual?: boolean
    isDefault?: boolean
    deviceType?: 'consumption' | 'generation'
    source?: string
    key: string
    reference: { id: string; type: string }
    energyType: string
    unit: string
    resolution: {
      enum: string
      label: string
      minutes: number
    }
    minDate: string | null
    maxDate: string | null
    dataQualityTimeSeriesIds: string[]
    group?: TimeSeriesOptionGroup
    selectable: boolean
    disabledReason?: string
    formulaVersion?: string
  }[]
}

interface IUseTimeSeriesSelectDataProps {
  excludeTimeSeriesIds?: string[]
  /** Series types (by string value, e.g. 'custom', 'special', 'temperature') to hide from the list. */
  excludeSeriesTypes?: string[]
  /**
   * When set, only series whose normalised source granularity is in this list are shown.
   * e.g. `[15]` keeps 15-min (and sub-15-min, which normalise to 15) series and hides hourly-and-coarser
   * ones (reporting heatmap, EC-5669). Compared against `getNormalizedSourceGranularityMinutes` (EC-5744).
   */
  includeSourceGranularityMinutes?: number[]
}

export const useTimeSeriesSelectData = ({
  excludeTimeSeriesIds = [],
  excludeSeriesTypes,
  includeSourceGranularityMinutes,
}: IUseTimeSeriesSelectDataProps) => {
  const { series } = useSeriesContext()

  const allMappedSeries = useMemo(() => {
    return series.map(ts => {
      const locationId = Number(ts.locationId)
      const buildingId = ts.buildingId ? Number(ts.buildingId) : undefined
      const seriesType = (
        (ts.type as string) === 'temperature_celsius' || (ts.id as string) === 'temperature_celsius' ? SeriesTypeEnum.temperature : ts.type
      ) as SeriesTypeEnum
      const reference = { id: String(ts.id), type: String(ts.type) }
      const timeSerie: ITimeSeriesData['timeSeries'][0] = {
        id: String(ts.id),
        name: ts.name,
        locationId,
        buildingId,
        seriesType,
        label: ts.label,
        isVirtual: ts.isVirtual,
        isDefault: ts.isDefault,
        deviceType: ts.deviceType,
        source: ts.source,
        key: getTimeSeriesSelectionKey(reference, locationId, buildingId),
        reference,
        energyType: ts.energyType,
        unit: ts.unit,
        resolution: ts.resolution,
        minDate: ts.minDate,
        maxDate: ts.maxDate,
        dataQualityTimeSeriesIds: [String(ts.id)],
        selectable: true,
      }
      return timeSerie
    })
  }, [series])

  // Use string keys for array deps so inline array literals (e.g. ['special','temperature'])
  // don't produce new references on every parent render and trigger the selector's reset effect.
  const excludeTimeSeriesIdsKey = excludeTimeSeriesIds?.join(',') ?? ''
  const excludeSeriesTypesKey = excludeSeriesTypes?.join(',') ?? ''
  const includeSourceGranularityMinutesKey = includeSourceGranularityMinutes?.join(',') ?? ''
  const mappedSeries = useMemo(() => {
    const allowedGranularityMinutes = includeSourceGranularityMinutes?.length ? new Set(includeSourceGranularityMinutes) : null
    const sourceGranularityMinutesById = allowedGranularityMinutes
      ? new Map(series.map(ts => [String(ts.id), getNormalizedSourceGranularityMinutes(ts.resolution?.minutes)]))
      : null
    const matchesGranularityFilter = (id: string): boolean => {
      if (!allowedGranularityMinutes) return true
      const sourceGranularityMinutes = sourceGranularityMinutesById?.get(id)
      // Series without a known source granularity are dropped while a granularity filter is active.
      return sourceGranularityMinutes != null && allowedGranularityMinutes.has(sourceGranularityMinutes)
    }
    return allMappedSeries.filter(
      ts =>
        !excludeTimeSeriesIds?.includes(ts.id) &&
        (!excludeSeriesTypes?.length || !excludeSeriesTypes.includes(String(ts.seriesType))) &&
        matchesGranularityFilter(ts.id)
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allMappedSeries, excludeTimeSeriesIdsKey, excludeSeriesTypesKey, includeSourceGranularityMinutesKey])

  return { timeSeries: mappedSeries, allTimeSeries: allMappedSeries }
}
